import { HeadlessUI, Hint } from '@systemeio/ui'
import AffiliateDashboardPromotionLink from 'modules/affiliate-dashboard/components/affiliate-dashboard-promotion-link'
import { useAffiliateDashboardPromotion } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-promotion'
import { AffiliateDashboardPromotionInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-promotion-interface'
import React, { useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import ArrowDownIcon from 'shared/icons/arrow-down-icon'

interface AffiliatePromotionProps {
  affiliateId?: string
}

const AffiliateDashboardPromotion = ({ affiliateId }: AffiliatePromotionProps) => {
  const { t } = useLocoTranslation()

  const [opened, setOpened] = useState(false)

  const { data: affiliatePromotion } = useAffiliateDashboardPromotion(opened)

  return (
    <div className={'flex flex-col gap-3 w-full bg-white rounded-lg overflow-x-hidden px-6 py-4'}>
      <div className={'flex flex-col gap-1'}>
        <div className={'flex gap-3 justify-between'}>
          <span className={'text-darkblue font-bold text-[17px]'}>
            {t('dashboard.affiliate_dashboard.promote_first')}
          </span>
          <button
            className={
              'outline-none rounded-md focus-visible:ring-2 ring-blue ring-offset-1 flex gap-1.5 items-center'
            }
            onClick={() => setOpened(prev => !prev)}
          >
            <span className={'hidden lg:inline text-blue'}>
              {opened
                ? t('dashboard.affiliate_dashboard.hide_stats')
                : t('dashboard.affiliate_dashboard.show_stats')}
            </span>
            <ArrowDownIcon
              className={`transition-transform ${opened && 'rotate-180'} stroke-blue`}
            />
          </button>
        </div>
        <span className={'text-darkblue'}>{t('dashboard.affiliate_dashboard.promote_second')}</span>
      </div>
      <AffiliateDashboardPromotionLink affiliateId={affiliateId} />
      <HeadlessUI.Transition
        show={opened}
        enter="ease-linear duration-200"
        enterFrom="-translate-y-1 opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="ease-linear duration-200"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="-translate-y-1 opacity-0"
      >
        <div className={'flex gap-3 justify-between'}>
          <AffiliatePromotionContent
            affiliatePromotion={affiliatePromotion}
            title={
              <Hint label={t('dashboard.affiliate.last_30_days')}>
                {t('dashboard.affiliate_dashboard.freemium_registrations')}
              </Hint>
            }
          >
            {affiliatePromotion => affiliatePromotion.freeRegistrationsCount}
          </AffiliatePromotionContent>
          <AffiliatePromotionContent
            affiliatePromotion={affiliatePromotion}
            title={
              <Hint label={t('dashboard.affiliate.current_active')}>
                {t('dashboard.affiliate_dashboard.active_customers')}
              </Hint>
            }
          >
            {affiliatePromotion => affiliatePromotion.paidCount}
          </AffiliatePromotionContent>
        </div>
      </HeadlessUI.Transition>
    </div>
  )
}

const AffiliatePromotionContent = ({
  affiliatePromotion,
  children,
  title,
}: {
  affiliatePromotion?: AffiliateDashboardPromotionInterface
  children: (affiliatePromotion: AffiliateDashboardPromotionInterface) => string | number
  title: string | JSX.Element
}) => (
  <div className={'flex flex-col gap-3 w-1/2 justify-between'}>
    <span className={'text-darkblue'}>{title}</span>
    {affiliatePromotion ? (
      <span className={'text-darkblue text-[17px] font-bold'}>{children(affiliatePromotion)}</span>
    ) : (
      <span className={'animate-pulse w-fit bg-gray-600 rounded-lg text-transparent px-5'}>.</span>
    )}
  </div>
)

export default AffiliateDashboardPromotion
